import {useCallback, useEffect, useRef, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {rfiService} from '@/services/rfiService';
import type {BusinessRFI, BusinessRFISource, BusinessRFIStatus} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';
import {logRejection} from '@/utils/logRejection';

interface FetchParams {
  page?: number;
  limit?: number;
}

export const useBusinessRFIList = (businessId: string) => {
  const [list, setList] = useState<BusinessRFI[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});

  const filtersToApplyRef = useRef<AfriexQueryParams>({});
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchList = useCallback(
    async (params: FetchParams = {}) => {
      if (!businessId) {
        return;
      }
      try {
        setError(null);
        setIsLoading(true);
        const {data: rfis, total: count} = await rfiService.getBusinessRFIList(
          businessId,
          {
            page: params.page ?? 0,
            limit: params.limit ?? 10,
            status: currentFilters.status as BusinessRFIStatus | undefined,
            source: currentFilters.source as BusinessRFISource | undefined,
            customerEmail: currentFilters.customerEmail,
          },
        );
        setList(rfis ?? []);
        setTotal(count ?? 0);
        setPage(params.page ?? 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [businessId, currentFilters],
  );

  // Refetch (reset to first page) when the business or the active filters change.
  useEffect(() => {
    // `fetchList` catches into `error` state, so this only fires if that catch
    // or the finally itself throws. Log rather than swallow silently.
    fetchList({page: 0}).catch(
      logRejection('Failed to load business RFI list'),
    );
    setIsPageReset((prev) => !prev);
  }, [fetchList]);

  // Applied-immediately filters (status).
  const selectCurrentFilters = useCallback(
    (name: string, value: string | undefined) => {
      setCurrentFilters((prev) => ({...prev, [name]: value}));
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Staged filters (search) — committed via handleApplyFilters.
  const selectAppliedFilters = useCallback(
    (name: string, value: string | undefined) => {
      const next = {...filtersToApplyRef.current, [name]: value};
      filtersToApplyRef.current = next;
      setFiltersToApply(next);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    applyFilters({...currentFilters, ...filtersToApplyRef.current});
    setIsPageReset((prev) => !prev);
  }, [applyFilters, currentFilters]);

  // SearchBar X / Clear — resets ALL filters (search + status).
  const clearFilters = useCallback(() => {
    filtersToApplyRef.current = {};
    setFiltersToApply({});
    setCurrentFilters({});
    setIsPageReset((prev) => !prev);
  }, []);

  return {
    list,
    pageInfo: {total, page},
    isLoading,
    isPageReset,
    error,
    fetchList,
    currentFilters,
    filtersToApply,
    selectCurrentFilters,
    selectAppliedFilters,
    handleApplyFilters,
    clearFilters,
  };
};
